import * as Immutable from 'immutable'
import * as React from 'react'
import { Connection } from '../../api/graphql'
import {
  Area,
  AssetsOutput,
  Attachment,
  RentalAdvert,
  Route,
  RoutesOutput,
  SprocketAsset,
  Theme
} from '../../types/types'
import CoronaPolicySticker from './components/CoronaPolicySticker'
import Facebook from './components/Facebook'
import HeroTitle from './components/HeroTitle'
import HeroVisual from './components/HeroVisual'
import HighlightedAdverts from './components/HighlightedAdverts'
import HighlightedAreas from './components/HighlightedAreas'
import HighlightedThemes from './components/HighlightedThemes'
import IntroSummary from './components/IntroSummary'
import LastMinuteSticker from './components/LastMinuteSticker'
import QuickFilter, { QuickFilterProps } from './components/QuickFilter'
import ZooverBanner from './components/ZooverBanner'

export type HomeRoutes = {
  lastminutes: Route
  corona_policy: Route
  gites: Route
  areas: Route
  themes: Route
}

export type HomeAssets = {
  'gites-zoover-award-2019.png': SprocketAsset
  'home/last-minutes-sticker.svg': SprocketAsset
}

type Props = {
  routes: RoutesOutput<HomeRoutes>
  headerImage: Attachment
  assets: AssetsOutput<HomeAssets>
  highlightAdverts: Connection<RentalAdvert>
  highlightAreas: Connection<Area>
  highlightThemes: Connection<Theme>
  contentAboveCarousel: string
  readMoreOpen: boolean
  toggleReadMore: (_: boolean) => void
  showFacebookPagePlugin: boolean
  onFacebookClose: () => void
  filter: QuickFilterProps
  mobileFilter: QuickFilterProps
}

type State = {
  showMobileFilter: boolean
  showDesktopFilter: boolean
}

export class Index extends React.Component<Props, State> {
  mobileContainerRef: React.RefObject<HTMLDivElement>
  desktopContainerRef: React.RefObject<HTMLDivElement>
  resizeTimer: number

  constructor(props: Props, context) {
    super(props, context)
    this.state = {
      showMobileFilter: false,
      showDesktopFilter: false
    }

    this.mobileContainerRef = React.createRef()
    this.desktopContainerRef = React.createRef()
    this.onResize = this.onResize.bind(this)
  }

  componentDidMount() {
    this.updateShowState()
    window.addEventListener('resize', this.onResize)
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.onResize)
  }

  render() {
    const contents = Immutable.List<string>(
      this.props.contentAboveCarousel.split('<br />')
    )
    return (
      <>
        <div className="hero hero--home">
          <HeroVisual image={this.props.headerImage} />
          <div className="hero__visual--overlay" />
          <div
            className="hero__container hide-from-tablet"
            ref={this.mobileContainerRef}
          >
            {this.state.showMobileFilter ? (
              <div className="hero__wrapper">
                <div className="hero__inner">
                  <div className="hero__content">
                    <div className="hero__title-wrap">
                      <h1
                        className="hero__title hero__title--home"
                        dangerouslySetInnerHTML={{ __html: I18n.t('header') }}
                      />
                    </div>
                  </div>
                  <div className="search search--home search--full-width">
                    <div className="grid-container">
                      <div className="grid-100">
                        <QuickFilter
                          startDate={this.props.filter.startDate}
                          endDate={this.props.filter.endDate}
                          onFocusChange={fi =>
                            this.props.filter.onFocusChange(fi)
                          }
                          focusedInput={this.props.filter.focusedInput}
                          onDatesChange={range =>
                            this.props.filter.onDatesChange(range)
                          }
                          filterDisabled={this.props.filter.filterDisabled}
                          onFilterSubmit={() =>
                            this.props.filter.onFilterSubmit()
                          }
                          maxPeople={this.props.filter.maxPeople}
                          onMaxPeopleChange={i =>
                            this.props.filter.onMaxPeopleChange(i)
                          }
                        />
                      </div>
                    </div>
                  </div>
                  <LastMinuteSticker
                    kind="mobile"
                    route={this.props.routes.get('lastminutes')}
                  />
                </div>
              </div>
            ) : (
              <></>
            )}
          </div>
          <div
            className="hero__container hide-on-mobile"
            ref={this.desktopContainerRef}
          >
            {this.state.showDesktopFilter ? (
              <div className="hero__wrapper">
                <div className="hero__inner">
                  <div className="hero__content">
                    <div className="grid-40 tablet-grid-50 mobile-grid-100">
                      <div className="posr">
                        <LastMinuteSticker
                          kind="desktop"
                          route={this.props.routes.get('lastminutes')}
                          asset={this.props.assets.get(
                            'home/last-minutes-sticker.svg'
                          )}
                        />
                        <HeroTitle />
                      </div>
                    </div>
                    <div className="search search--home">
                      <div className="grid-container">
                        <div className="grid-100">
                          <QuickFilter
                            startDate={this.props.mobileFilter.startDate}
                            endDate={this.props.mobileFilter.endDate}
                            onFocusChange={fi =>
                              this.props.mobileFilter.onFocusChange(fi)
                            }
                            focusedInput={this.props.mobileFilter.focusedInput}
                            onDatesChange={range =>
                              this.props.mobileFilter.onDatesChange(range)
                            }
                            filterDisabled={
                              this.props.mobileFilter.filterDisabled
                            }
                            onFilterSubmit={() =>
                              this.props.mobileFilter.onFilterSubmit()
                            }
                            maxPeople={this.props.mobileFilter.maxPeople}
                            onMaxPeopleChange={i =>
                              this.props.mobileFilter.onMaxPeopleChange(i)
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <></>
            )}
          </div>
        </div>

        <div className="page-section page-section--pt">
          <div className="grid-container">
            <div className="page-section__body page-section__houses">
              <div className="grid-100">
                <h2 className="h1 home__title">
                  {I18n.t('home.adverts_intro_title')}
                </h2>
                <IntroSummary
                  contents={contents}
                  readMoreOpen={this.props.readMoreOpen}
                  toggleReadMore={b => this.props.toggleReadMore(b)}
                />

                <div className="accommodations">
                  <HighlightedAdverts
                    adverts={Immutable.List(this.props.highlightAdverts.edges)
                      .map(x => x.node)
                      .toList()}
                  />
                </div>
              </div>
              <div className="grid-100 tac margin-top">
                <p>
                  <a
                    className="btn btn--primary btn--medium btn--wide"
                    href={this.props.routes.get('gites').path}
                  >
                    {I18n.t('home.adverts_overview_link')}
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="page-section page-section--pt">
          <div className="grid-container">
            <div className="grid-100">
              <div className="page-section__body">
                <h2 className="h1 home__title">
                  {I18n.t('home.area_intro_title')}
                </h2>
                <div className="highlights">
                  <HighlightedAreas
                    areas={Immutable.List(this.props.highlightAreas.edges)
                      .map(x => x.node)
                      .toList()}
                  />
                </div>
                <div className="grid-100 tac">
                  <p>
                    <a
                      className="btn btn--ghost-purple btn--medium btn--wide"
                      href={this.props.routes.get('areas').path}
                    >
                      {I18n.t('areas.overview.link')}
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="page-section page-section--award">
          <div className="grid-container">
            <div className="grid-100">
              <div className="zoover__banner">
                <ZooverBanner
                  image={this.props.assets.get('gites-zoover-award-2019.png')}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="page-section page-section--split">
          <div className="grid-container">
            <HighlightedThemes
              themes={Immutable.List(this.props.highlightThemes.edges)
                .map(x => x.node)
                .toList()}
            />
          </div>
        </div>
        <div className="page-section page-section--pt">
          <div className="grid-container themes">
            <div className="themes__cta">
              <p>{I18n.t('home.themes.text')}</p>
              <a
                className="btn btn--ghost-purple btn--medium btn--wide"
                href={this.props.routes.get('themes').url}
              >
                {I18n.t('home.themes.cta')}
              </a>
            </div>
          </div>
        </div>
        {this.props.showFacebookPagePlugin ? (
          <Facebook onClose={() => this.props.onFacebookClose()} />
        ) : (
          ''
        )}
      </>
    )
  }

  private onResize() {
    if (this.resizeTimer) {
      window.clearTimeout(this.resizeTimer)
    }
    this.resizeTimer = window.setTimeout(() => this.updateShowState(), 100)
  }

  private isVisible(element: React.RefObject<HTMLDivElement>): boolean {
    return (
      element.current.offsetWidth !== 0 || element.current.offsetHeight !== 0
    )
  }

  private updateShowState() {
    this.setState({
      showDesktopFilter: this.isVisible(this.desktopContainerRef),
      showMobileFilter: this.isVisible(this.mobileContainerRef)
    })
  }
}
